import { DayPilot, DayPilotMonth } from "@daypilot/daypilot-lite-react";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CalendarToolbar } from "./components/CalendarToolbar";
import { CalendarFilters } from "./components/CalendarFilters";
import { NewReportModal } from "./components/NewReportModal";
import { useCalendarNavigation } from "./hooks/useCalendarNavigation";
import { useCalendarData } from "./hooks/useCalendarData";
import { useCalendarFilters } from "./hooks/useCalendarFilters";
import { transformReportsToEvents } from "./utils/eventTransformer";
import { toDayPilotDate } from "./utils/calendarHelpers";
import { calendarConfig } from "./config/calendarConfig";
import { successToast } from "../../helpers/successToast";
import { PlannedReport } from "../../api/reportsApi";
import { getPlannedVisitPeople } from "../../utils/plannedVisitUtils";

export const CalendarPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [calendarRef, setCalendarRef] = useState<any>(null);
  const [showNewReportModal, setShowNewReportModal] = useState(false);
  const [showEditPlannedModal, setShowEditPlannedModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedPlannedReport, setSelectedPlannedReport] = useState<PlannedReport | null>(null);
  
  const {
    currentMonth,
    handlePreviousMonth,
    handleNextMonth,
    handleToday,
  } = useCalendarNavigation();

  const {
    selectedStudy,
    selectedEmail,
    handleStudyChange,
    handleEmailChange,
  } = useCalendarFilters();

  const { reports, plannedReports, isLoading, error } = useCalendarData(currentMonth);

  // Filter reports based on selected filters
  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      if (selectedStudy && report.studyName !== selectedStudy) return false;
      if (selectedEmail && report.createdBy !== selectedEmail) return false;
      return true;
    });
  }, [reports, selectedStudy, selectedEmail]);

  // Filter planned reports based on selected filters
  const filteredPlannedReports = useMemo(() => {
    return plannedReports.filter(planned => {
      if (selectedStudy && planned.studyName !== selectedStudy) return false;
      if (selectedEmail && !getPlannedVisitPeople(planned).includes(selectedEmail)) return false;
      return true;
    });
  }, [plannedReports, selectedStudy, selectedEmail]);

  // Update calendar display when currentMonth changes
  useEffect(() => {
    if (calendarRef && calendarRef.control && !calendarRef.control.disposed) {
      try {
        calendarRef.control.startDate = new DayPilot.Date(toDayPilotDate(currentMonth));
        calendarRef.control.update();
      } catch (error) {
        console.warn('Failed to update calendar:', error);
      }
    }
  }, [currentMonth, calendarRef]);

  const events = useMemo(
    () => transformReportsToEvents(filteredReports, filteredPlannedReports, t),
    [filteredReports, filteredPlannedReports, t]
  );

  // Handlers for new report modal
  const handleTimeRangeSelected = (args: any) => {

    const selectedDate = new Date(args.start.value);
    const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

    setSelectedDate(date);
    setShowNewReportModal(true);
  };

  const handleReportCreated = () => {
    successToast();
  };

  const handleAddReport = () => {
    setSelectedDate(new Date());
    setShowNewReportModal(true);
  };

  // Event click handler - needs to be outside useState to access current state
  const handleEventClick = (args: any) => {
    const eventId = args.e.data.id;
    const isPlannedEvent = events.find(e => e.id === eventId)?.isPlanned;
    
    if (isPlannedEvent) {
      // Navigate to visit page
      navigate(`/visit/${eventId}`);
    } else {
      // Open regular report in new tab
      window.open(`/reports/${eventId}`, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">{t("loadingCalendar")}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-red-600">
          {t("errorLoadingCalendar")}: {(error as Error).message}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Page Title */}
      <div className="mb-4">
        <h1 className="text-[15px] font-semibold text-[var(--sc-text)]">{t("reportsCalendar")}</h1>
        <p className="mt-1 text-sm text-[var(--sc-muted)]">
          {t("viewAllReportsByVisitDates")}
        </p>
      </div>

      {/* Filters */}
      <div className="mb-4 p-4 bg-[var(--sc-bg)] rounded-[10px] border border-[var(--sc-border)]">
        <CalendarFilters
          reports={reports}
          plannedReports={plannedReports}
          selectedStudy={selectedStudy}
          selectedEmail={selectedEmail}
          onStudyChange={handleStudyChange}
          onEmailChange={handleEmailChange}
        />
      </div>

      {/* Sticky Navigation and Status Bar */}
      <CalendarToolbar
        onPreviousMonth={handlePreviousMonth}
        onNextMonth={handleNextMonth}
        onToday={handleToday}
        onAddReport={handleAddReport}
        plannedReportsCount={filteredPlannedReports.length}
        currentMonth={currentMonth}
      />

      {/* Calendar */}
      <div className="bg-[var(--sc-surface)] rounded-[10px] border border-[var(--sc-border)] calendar-container">
        <DayPilotMonth 
          {...calendarConfig}
          events={events} 
          startDate={new DayPilot.Date(toDayPilotDate(currentMonth))}
          key={`${currentMonth.getFullYear()}-${currentMonth.getMonth()}`}
          ref={setCalendarRef}
          onEventClick={handleEventClick}
          onTimeRangeSelected={handleTimeRangeSelected}
        />
      </div>

      {/* New Report Modal */}
      <NewReportModal
        isOpen={showNewReportModal}
        onClose={() => setShowNewReportModal(false)}
        selectedDate={selectedDate}
        onReportCreated={handleReportCreated}
      />

      {/* Edit Planned Report Modal */}
      {/* <EditPlannedReportModal
        isOpen={showEditPlannedModal}
        onClose={() => setShowEditPlannedModal(false)}
        plannedReport={selectedPlannedReport}
        onReportUpdated={handleReportCreated}
      /> */}
    </div>
  );
};